import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppShell, Sidebar, TopBar, StatusBar, ParticleBackground } from "@pcc/ui";
import { navGroups } from "./components/nav-config.js";
import { useUIStore } from "./stores/ui-store.js";
import { useAuthStore, onIdentityChange } from "./stores/auth-store.js";
import { LoginPage } from "./pages/LoginPage.js";
import { PageTransition } from "./components/PageTransition.js";
import { NotificationToasts } from "./components/NotificationToasts.js";
import { OnboardingTour, TourRestartButton } from "./components/OnboardingTour.js";
import { WalletProvider } from "./providers/WalletProvider.js";
import { ConnectWallet } from "./components/ConnectWallet.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { ModeToggle } from "./components/ModeToggle.js";
import { Sentry } from "./lib/telemetry.js";
import { usePageTracking } from "./hooks/use-page-tracking.js";
import { SpatialApp } from "./SpatialApp.js";
import { AgentLandingHero } from "./components/AgentLandingHero.js";

// ---------------------------------------------------------------------------
// Lazy-loaded pages (code-split per route)
// ---------------------------------------------------------------------------

const AgentChatPage = lazy(() => import("./pages/AgentChatPage.js").then(m => ({ default: m.AgentChatPage })));
const LandingPage = lazy(() => import("./pages/LandingPage.js").then(m => ({ default: m.LandingPage })));
const StartPage = lazy(() => import("./pages/StartPage.js").then(m => ({ default: m.StartPage })));
const EarnFromYourWorkPage = lazy(() => import("./pages/EarnFromYourWorkPage.js").then(m => ({ default: m.EarnFromYourWorkPage })));
const AgentLinkPage = lazy(() => import("./pages/AgentLinkPage.js").then(m => ({ default: m.AgentLinkPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage.js").then(m => ({ default: m.DashboardPage })));
const DiscoverPage = lazy(() => import("./pages/DiscoverPage.js").then(m => ({ default: m.DiscoverPage })));
const BuilderPage = lazy(() => import("./pages/BuilderPage.js").then(m => ({ default: m.BuilderPage })));
const WorkflowPage = lazy(() => import("./pages/WorkflowPage.js").then(m => ({ default: m.WorkflowPage })));
const JobsPage = lazy(() => import("./pages/JobsPage.js").then(m => ({ default: m.JobsPage })));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage.js").then(m => ({ default: m.JobDetailPage })));
const JobFlowPage = lazy(() => import("./pages/JobFlowPage.js").then(m => ({ default: m.JobFlowPage })));
const KernelsPage = lazy(() => import("./pages/KernelsPage.js").then(m => ({ default: m.KernelsPage })));
const KernelDetailPage = lazy(() => import("./pages/KernelDetailPage.js").then(m => ({ default: m.KernelDetailPage })));
const EscrowPage = lazy(() => import("./pages/EscrowPage.js").then(m => ({ default: m.EscrowPage })));
const AgentLogPage = lazy(() => import("./pages/AgentLogPage.js").then(m => ({ default: m.AgentLogPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage.js").then(m => ({ default: m.SettingsPage })));
const OnboardLandingPage = lazy(() => import("./pages/OnboardLandingPage.js").then(m => ({ default: m.OnboardLandingPage })));
const OnboardWizardPage = lazy(() => import("./pages/OnboardWizardPage.js").then(m => ({ default: m.OnboardWizardPage })));
const OnboardChatPage = lazy(() => import("./pages/OnboardChatPage.js").then(m => ({ default: m.OnboardChatPage })));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage.js").then(m => ({ default: m.MarketplacePage })));
const MarketplaceDetailPage = lazy(() => import("./pages/MarketplaceDetailPage.js").then(m => ({ default: m.MarketplaceDetailPage })));
const ROICalculatorPage = lazy(() => import("./pages/ROICalculatorPage.js").then(m => ({ default: m.ROICalculatorPage })));
const SpaceFinderPage = lazy(() => import("./pages/SpaceFinderPage.js").then(m => ({ default: m.SpaceFinderPage })));
const SpaceDetailPage = lazy(() => import("./pages/SpaceDetailPage.js").then(m => ({ default: m.SpaceDetailPage })));
const OperatorDashboardPage = lazy(() => import("./pages/OperatorDashboardPage.js").then(m => ({ default: m.OperatorDashboardPage })));
const OperatorMachineDetailPage = lazy(() => import("./pages/OperatorMachineDetailPage.js").then(m => ({ default: m.OperatorMachineDetailPage })));
const RevenueDashboardPage = lazy(() => import("./pages/RevenueDashboardPage.js").then(m => ({ default: m.RevenueDashboardPage })));
const SensorDashboardPage = lazy(() => import("./pages/SensorDashboardPage.js").then(m => ({ default: m.SensorDashboardPage })));
const BatchTrackingPage = lazy(() => import("./pages/BatchTrackingPage.js").then(m => ({ default: m.BatchTrackingPage })));
const EvidenceExplorerPage = lazy(() => import("./pages/EvidenceExplorerPage.js").then(m => ({ default: m.EvidenceExplorerPage })));
const ProcessLogsPage = lazy(() => import("./pages/ProcessLogsPage.js").then(m => ({ default: m.ProcessLogsPage })));
const LogisticsHubPage = lazy(() => import("./pages/LogisticsHubPage.js").then(m => ({ default: m.LogisticsHubPage })));
const ShipmentDetailPage = lazy(() => import("./pages/ShipmentDetailPage.js").then(m => ({ default: m.ShipmentDetailPage })));
const SpaceBookingsPage = lazy(() => import("./pages/SpaceBookingsPage.js").then(m => ({ default: m.SpaceBookingsPage })));
const InstallationDetailPage = lazy(() => import("./pages/InstallationDetailPage.js").then(m => ({ default: m.InstallationDetailPage })));
const DeviceBuilderPage = lazy(() => import("./pages/DeviceBuilderPage.js").then(m => ({ default: m.DeviceBuilderPage })));
const SetupWizardPage = lazy(() => import("./pages/SetupWizardPage.js").then(m => ({ default: m.SetupWizardPage })));
const SetupAgentPage = lazy(() => import("./pages/SetupAgentPage.js").then(m => ({ default: m.SetupAgentPage })));
const OrchestratorPage = lazy(() => import("./pages/OrchestratorPage.js").then(m => ({ default: m.OrchestratorPage })));
const OrchestratorDetailPage = lazy(() => import("./pages/OrchestratorDetailPage.js").then(m => ({ default: m.OrchestratorDetailPage })));
const ProtocolLibraryPage = lazy(() => import("./pages/ProtocolLibraryPage.js").then(m => ({ default: m.ProtocolLibraryPage })));
const ProtocolDetailPage = lazy(() => import("./pages/ProtocolDetailPage.js").then(m => ({ default: m.ProtocolDetailPage })));
const ProtocolBuilderPage = lazy(() => import("./pages/ProtocolBuilderPage.js").then(m => ({ default: m.ProtocolBuilderPage })));
const ProtocolRunPage = lazy(() => import("./pages/ProtocolRunPage.js").then(m => ({ default: m.ProtocolRunPage })));
const SubnetStatusPage = lazy(() => import("./pages/SubnetStatusPage.js").then(m => ({ default: m.SubnetStatusPage })));
const SettlementPage = lazy(() => import("./pages/SettlementPage.js").then(m => ({ default: m.SettlementPage })));
const OnboardKitPage = lazy(() => import("./pages/OnboardKitPage.js").then(m => ({ default: m.OnboardKitPage })));
const TelemetryPage = lazy(() => import("./pages/TelemetryPage.js").then(m => ({ default: m.TelemetryPage })));
const TracesPage = lazy(() => import("./pages/TracesPage.js").then(m => ({ default: m.TracesPage })));
const NegotiationPage = lazy(() => import("./pages/NegotiationPage.js").then(m => ({ default: m.NegotiationPage })));
const OperatorMobilePage = lazy(() => import("./pages/OperatorMobilePage.js").then(m => ({ default: m.OperatorMobilePage })));
const EconomicAgreementsPage = lazy(() => import("./pages/EconomicAgreementsPage.js").then(m => ({ default: m.EconomicAgreementsPage })));
const WalletPage = lazy(() => import("./pages/WalletPage.js").then(m => ({ default: m.WalletPage })));
const WalletLinkPage = lazy(() => import("./pages/WalletLinkPage.js").then(m => ({ default: m.WalletLinkPage })));
const WhitepaperPage = lazy(() => import("./pages/WhitepaperPage.js").then(m => ({ default: m.WhitepaperPage })));
const BatchBoardPage = lazy(() => import("./pages/BatchBoardPage.js").then(m => ({ default: m.BatchBoardPage })));
const NegotiationSessionPage = lazy(() => import("./pages/NegotiationSessionPage.js").then(m => ({ default: m.NegotiationSessionPage })));
const AgentPackagePage = lazy(() => import("./pages/AgentPackagePage.js").then(m => ({ default: m.AgentPackagePage })));
const SponsorTelemetryPage = lazy(() => import("./pages/SponsorTelemetryPage.js").then(m => ({ default: m.SponsorTelemetryPage })));
const SystemDashboardPage = lazy(() => import("./pages/SystemDashboardPage.js").then(m => ({ default: m.SystemDashboardPage })));
const AnalyticsDashboardPage = lazy(() => import("./pages/AnalyticsDashboardPage.js").then(m => ({ default: m.AnalyticsDashboardPage })));
const KernelLeaderboardPage = lazy(() => import("./pages/KernelLeaderboardPage.js").then(m => ({ default: m.KernelLeaderboardPage })));
const RateSchedulePublishPage = lazy(() => import("./pages/RateSchedulePublishPage.js").then(m => ({ default: m.RateSchedulePublishPage })));
const RateScheduleViewPage = lazy(() => import("./pages/RateScheduleViewPage.js").then(m => ({ default: m.RateScheduleViewPage })));

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-pulse text-emerald-400/60 text-sm tracking-wide">Loading…</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Query client
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// A cached read belongs to the identity that made it: clear the cache whenever the signed-in
// identity changes, so the next identity never sees the previous one's jobs or money.
onIdentityChange(() => queryClient.clear());

// ---------------------------------------------------------------------------
// Agent Chat Shell — full-height chat, no sidebar
// ---------------------------------------------------------------------------

function AgentShell() {
  const { currentPageTitle, currentPageSubtitle } = useUIStore();
  usePageTracking();

  return (
    <div className="flex flex-col h-screen bg-black/90 relative">
      <ParticleBackground />
      <div className="relative z-10 flex flex-col h-full">
        {/* Minimal top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06] bg-black/40 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-white/70">{currentPageTitle}</div>
            {currentPageSubtitle && (
              <div className="text-xs text-white/30">{currentPageSubtitle}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ModeToggle />
            <ConnectWallet />
          </div>
        </div>
        {/* Chat content */}
        <Suspense fallback={<PageLoader />}>
          <AgentChatPage />
        </Suspense>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Shell — full 45-page shell with sidebar
// ---------------------------------------------------------------------------

function LogoutButton() {
  const logout = useAuthStore((s) => s.signOutWallet);
  return (
    <button
      onClick={() => void logout()}
      className="w-full px-3 py-2 text-[10px] text-white/25 hover:text-red-400/70 hover:bg-white/[0.03] rounded-lg transition-all text-left tracking-wide uppercase"
    >
      Disconnect
    </button>
  );
}

function DashboardShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar, currentPageTitle, currentPageSubtitle } = useUIStore();
  usePageTracking();

  return (
    <>
      <AppShell
        particles={<ParticleBackground />}
        sidebar={
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <Sidebar
                groups={navGroups}
                currentPath={location.pathname}
                onNavigate={navigate}
                collapsed={sidebarCollapsed}
                onToggle={toggleSidebar}
              />
            </div>
            <div className="border-t border-white/[0.06] p-2">
              <LogoutButton />
            </div>
          </div>
        }
        topBar={
          <TopBar
            title={currentPageTitle}
            subtitle={currentPageSubtitle}
            actions={<><ModeToggle /><ConnectWallet /><TourRestartButton /></>}
          />
        }
        statusBar={<StatusBar kernelsOnline={2} activeJobs={3} networkStatus="connected" />}
      >
        <PageTransition>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/build" element={<BuilderPage />} />
              <Route path="/build/new-device" element={<DeviceBuilderPage />} />
              <Route path="/build/:type" element={<BuilderPage />} />
              <Route path="/workflow" element={<WorkflowPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage />} />
              <Route path="/kernels" element={<KernelsPage />} />
              <Route path="/kernels/leaderboard" element={<KernelLeaderboardPage />} />
              <Route path="/kernels/:kernelId" element={<KernelDetailPage />} />
              <Route path="/escrow" element={<EscrowPage />} />
              <Route path="/settlement" element={<SettlementPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/agents" element={<AgentLogPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/onboard" element={<OnboardLandingPage />} />
              <Route path="/onboard/kit" element={<OnboardKitPage />} />
              <Route path="/onboard/wizard" element={<OnboardWizardPage />} />
              <Route path="/onboard/wizard/:step" element={<OnboardWizardPage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/marketplace/roi" element={<ROICalculatorPage />} />
              <Route path="/marketplace/:classId" element={<MarketplaceDetailPage />} />
              <Route path="/spaces" element={<SpaceFinderPage />} />
              <Route path="/spaces/:spaceId" element={<SpaceDetailPage />} />
              <Route path="/operator" element={<OperatorDashboardPage />} />
              <Route path="/operator/revenue" element={<RevenueDashboardPage />} />
              <Route path="/operator/:machineId" element={<OperatorMachineDetailPage />} />
              <Route path="/sensors" element={<SensorDashboardPage />} />
              <Route path="/sensors/:kernelId" element={<SensorDashboardPage />} />
              <Route path="/batches" element={<BatchTrackingPage />} />
              <Route path="/batches/:batchId" element={<BatchTrackingPage />} />
              <Route path="/evidence" element={<EvidenceExplorerPage />} />
              <Route path="/evidence/:bundleId" element={<EvidenceExplorerPage />} />
              <Route path="/logs" element={<ProcessLogsPage />} />
              <Route path="/logistics" element={<LogisticsHubPage />} />
              <Route path="/logistics/shipments/:shipmentId" element={<ShipmentDetailPage />} />
              <Route path="/logistics/bookings" element={<SpaceBookingsPage />} />
              <Route path="/logistics/bookings/:bookingId" element={<SpaceBookingsPage />} />
              <Route path="/logistics/installations" element={<InstallationDetailPage />} />
              <Route path="/logistics/installations/:installationId" element={<InstallationDetailPage />} />
              <Route path="/orchestrator" element={<OrchestratorPage />} />
              <Route path="/orchestrator/:kernelId" element={<OrchestratorDetailPage />} />
              <Route path="/protocols" element={<ProtocolLibraryPage />} />
              <Route path="/protocols/new" element={<ProtocolBuilderPage />} />
              <Route path="/protocols/:templateId" element={<ProtocolDetailPage />} />
              <Route path="/protocols/:templateId/edit" element={<ProtocolBuilderPage />} />
              <Route path="/protocol-runs" element={<ProtocolRunPage />} />
              <Route path="/protocol-runs/:runId" element={<ProtocolRunPage />} />
              <Route path="/subnet" element={<SubnetStatusPage />} />
              {/* DePIN rewards and the Sovereign Wealth Fund pages rendered hard-coded treasuries, epochs,
                  claims and proposals with no API behind them (PX-12, product-steward #2559). Retired; the
                  real money surface is the agreements page. */}
              <Route path="/depin" element={<Navigate to="/economics" replace />} />
              <Route path="/swf/*" element={<Navigate to="/economics" replace />} />
              <Route path="/telemetry" element={<TelemetryPage />} />
              <Route path="/traces" element={<TracesPage />} />
              <Route path="/setup" element={<SetupWizardPage />} />
              <Route path="/setup/agent" element={<SetupAgentPage />} />
              <Route path="/negotiate" element={<NegotiationPage />} />
              <Route path="/negotiate/session" element={<NegotiationSessionPage />} />
              <Route path="/batch-board" element={<BatchBoardPage />} />
              <Route path="/agent-package" element={<AgentPackagePage />} />
              <Route path="/economics" element={<EconomicAgreementsPage />} />
              {/* The IP revenue page showed fabricated revenue beside a live Claim button (PX-12, N10). */}
              <Route path="/ip" element={<Navigate to="/economics" replace />} />
              <Route path="/sponsors" element={<SponsorTelemetryPage />} />
              <Route path="/system" element={<SystemDashboardPage />} />
              <Route path="/analytics" element={<AnalyticsDashboardPage />} />
              <Route path="/contributors/schedules/publish" element={<RateSchedulePublishPage />} />
              <Route path="/contributors/schedules/:hash" element={<RateScheduleViewPage />} />
            </Routes>
          </Suspense>
        </PageTransition>
      </AppShell>
      <NotificationToasts />
      <OnboardingTour />
    </>
  );
}

// ---------------------------------------------------------------------------
// Shell router — picks agent or dashboard shell based on mode
// ---------------------------------------------------------------------------

function Shell() {
  const interfaceMode = useUIStore((s) => s.interfaceMode);
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Public pages that don't require auth
  const publicPaths = ["/", "/start", "/whitepaper", "/go", "/earn", "/onboard/chat"];
  const isPublicPage = publicPaths.includes(location.pathname);

  // Login page at /login
  if (location.pathname === "/login") {
    return isAuthenticated ? (
      <Suspense fallback={<PageLoader />}><LandingPage /></Suspense>
    ) : (
      <LoginPage />
    );
  }

  // Auth gate — redirect to login for non-public pages
  if (!isAuthenticated && !isPublicPage) {
    return <LoginPage />;
  }

  if (location.pathname === "/link") {
    return (
      <Suspense fallback={<PageLoader />}>
        <WalletLinkPage />
      </Suspense>
    );
  }
  if (location.pathname === "/jobs/flow") {
    return <Suspense fallback={<PageLoader />}><JobFlowPage /></Suspense>;
  }

  // Landing page — agent-first (root)
  // AgentLandingHero is the eager, above-the-fold hero (onboard-ui aesthetic +
  // machine-readable agent entrypoints); the existing LandingPage renders below it.
  if (location.pathname === "/") {
    return (
      <Suspense fallback={<PageLoader />}>
        <AgentLandingHero />
        <LandingPage />
      </Suspense>
    );
  }

  if (location.pathname === "/start") {
    return (
      <Suspense fallback={<PageLoader />}>
        <StartPage />
      </Suspense>
    );
  }

  // Zero-friction contributor signup. Public — bundled wallet+APIkey+
  // schedule signup via POST /api/contributors/quickstart.
  if (location.pathname === "/earn") {
    return (
      <Suspense fallback={<PageLoader />}>
        <EarnFromYourWorkPage />
      </Suspense>
    );
  }

  if (location.pathname === "/whitepaper") {
    return (
      <Suspense fallback={<PageLoader />}>
        <WhitepaperPage />
      </Suspense>
    );
  }

  if (location.pathname === "/go") {
    return (
      <Suspense fallback={<PageLoader />}>
        <AgentLinkPage />
      </Suspense>
    );
  }

  // Conversational no-code onboarding (coord dc4d1ec8). Public so a layperson
  // can reach it from a marketing link without first signing in. The chat
  // page talks to POST /api/onboard/chat which is itself public on the gateway.
  if (location.pathname === "/onboard/chat") {
    return (
      <Suspense fallback={<PageLoader />}>
        <OnboardChatPage />
      </Suspense>
    );
  }

  if (location.pathname === "/operator/mobile") {
    return (
      <Suspense fallback={<PageLoader />}>
        <OperatorMobilePage />
      </Suspense>
    );
  }

  // /app and /app/* — Spatial fallback web dashboard (with agent prompt banner)
  if (location.pathname === "/app" || location.pathname.startsWith("/app/")) {
    return <SpatialApp />;
  }

  // Direct route to spatial interface (legacy compat)
  if (location.pathname === "/spatial") {
    return <SpatialApp />;
  }

  // /legacy/* — Legacy dashboard shell for bookmarked old routes
  if (location.pathname.startsWith("/legacy/")) {
    return <DashboardShell />;
  }

  if (interfaceMode === "spatial") {
    return <SpatialApp />;
  }

  if (interfaceMode === "agent") {
    return <AgentShell />;
  }

  return <DashboardShell />;
}

export function App() {
  return (
    // Sentry.ErrorBoundary captures errors to Sentry before falling through
    // to the local ErrorBoundary for display. When VITE_SENTRY_DSN is not set,
    // Sentry.init() was never called so this boundary is a transparent passthrough.
    <Sentry.ErrorBoundary showDialog={false}>
      <ErrorBoundary>
        <WalletProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
          </QueryClientProvider>
        </WalletProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}
